import type { FC, PropsWithChildren } from 'react';

import { Link } from '@backstage/core-components';
import {
  OptionalTooltip,
  ResolvedIcon,
} from '@ipen-portal/plugin-common-react';
import {
  Avatar,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { tss } from 'tss-react/mui';

import type { NavItem } from '../types';

const useStyles = tss.create(({ theme }) => ({
  activeText: {
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
  },
  avatar: {
    minWidth: 'auto',
  },
  icon: {
    background: 'unset',
    color: theme.palette.common.black,
    fontSize: 24,
  },
  icon2: {
    margin: theme.spacing(0.25, 0.5),
  },
  link: {
    textDecoration: 'none !important',
  },
  nestedIcon: {
    transform: 'scale(calc(16/24))',
  },
  root: {
    '.scrollable &': {
      paddingLeft: 6,
      paddingRight: 12,
    },
    'gap': 12,
    'padding': '6px 12px',
    'transition': theme.transitions.create('all', {
      duration: theme.transitions.duration.leavingScreen,
      easing: theme.transitions.easing.sharp,
    }),
  },
  selected: {
    '&:hover': {
      background: theme.palette.primary.dark,
    },
    'background': theme.palette.primary.main,
  },
  selectedIcon: {
    background: 'transparent',
    color: theme.palette.primary.contrastText,
    fontSize: 24,
  },
  selectedSubmenuIcon: {
    background: 'unset',
    color: theme.palette.primary.contrastText,
    fontSize: 24,
  },
}));

type Props = {
  'active'?: boolean;
  'aria-expanded'?: boolean;
  'classes'?: {
    root?: string;
    text?: string;
  };
  'isSubmenu'?: boolean;
  'item': Omit<NavItem, 'target'> & Partial<Pick<NavItem, 'target'>>;
  'nested'?: boolean;
  'onClick'?: () => void;
  'removeLeadingSlash'?: boolean;
};

export const NavMenuItem: FC<PropsWithChildren<Props>> = ({
  active = false,
  'aria-expanded': ariaExpanded,
  children,
  classes: classesOverride,
  isSubmenu = false,
  item,
  nested = false,
  onClick,
  removeLeadingSlash = false,
}) => {
  const { classes, cx } = useStyles();
  const { description, skipIconWrapping, target, title } = item;
  const iconElem = <ResolvedIcon icon={item.icon} />;
  let icon = nested
    ? (
        <span className={classes.nestedIcon}>{iconElem}</span>
      )
    : (
        iconElem || <span>&nbsp;</span>
      );
  icon = skipIconWrapping
    ? (
        <div className={classes.icon2}>{icon}</div>
      )
    : (
        <Avatar
          classes={{
            circular: isSubmenu
              ? classes.selectedSubmenuIcon
              : classes.selectedIcon,
            square: classes.icon,
          }}
          variant={active ? 'circular' : 'square'}
        >
          {icon}
        </Avatar>
      );

  let path = target ?? '';
  // TODO centralize this logic
  if (path.startsWith('ipenportal://')) {
    path = path.slice(13);
  } else if (removeLeadingSlash) {
    // And remove leading / for relative navigation
    path = path.replace(/^\//, '');
  }

  return (
    <OptionalTooltip title={description}>
      <ListItemButton
        aria-label={title}
        className={cx(
          classes.root,
          {
            [classes.selected]: active,
          },
          classesOverride?.root,
        )}
        onClick={onClick}
        role="button"
        {...(target !== undefined && {
          classes: { root: classes.link },
          component: Link,
          to: path,
        })}
        aria-expanded={ariaExpanded}
      >
        <ListItemAvatar className={classes.avatar}>{icon}</ListItemAvatar>
        <ListItemText
          classes={{
            primary: cx(
              {
                [classes.activeText]: active,
              },
              classesOverride?.text,
            ),
          }}
          primary={title}
          style={{
            lineHeight: 1,
          }}
        />
        {children}
      </ListItemButton>
    </OptionalTooltip>
  );
};
